import {AfterViewInit, Component, computed, DestroyRef, effect, inject, input, model, OnInit, output, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {CanvasBaseComponent} from "@common/components/canvas/canvas-base-legacy/canvas-base.component"
import {outputToObservable, takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {Hotkeys} from "@app/common/services/hotkeys/hotkeys.service"
import {PostProcessedImageData} from "@app/template-editor/components/template-image-viewer/template-image-viewer.component"

export type ImageViewState = {width: number; height: number; zoom: number; center: {x: number; y: number}}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-image-viewer-canvas",
    templateUrl: "./template-image-viewer-canvas.component.html",
    styleUrl: "./template-image-viewer-canvas.component.scss",
    imports: [CanvasBaseComponent],
})
export class TemplateImageViewerCanvasComponent implements OnInit, AfterViewInit {
    private readonly hotkeys = inject(Hotkeys)
    private readonly destroyRef = inject(DestroyRef)

    readonly $postProcessedImageData = input.required<PostProcessedImageData>({alias: "postProcessedImageData"})
    // Kept by the parent, because the canvas is recreated whenever the viewer shows something other than an image in between.
    readonly $viewState = model<ImageViewState | undefined>(undefined, {alias: "viewState"})

    readonly $canvasBase = viewChild.required<CanvasBaseComponent>("canvasBase")
    readonly loadingComplete = output<void>()

    protected readonly $imageData = computed(() => this.$postProcessedImageData().imageData)

    constructor() {
        effect(() => {
            const canvasBase = this.$canvasBase()
            this.$postProcessedImageData()

            canvasBase.requestRedraw()
        })
    }

    ngAfterViewInit() {
        const canvasBase = this.$canvasBase()
        let trackingView = false
        outputToObservable(canvasBase.loadingComplete)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const {navigation} = canvasBase
                const {width, height} = this.$imageData()
                const state = this.$viewState()
                if (state && state.width === width && state.height === height) {
                    navigation.zoomTo(state.zoom)
                    navigation.centerPosition(state.center.x, state.center.y)
                } else {
                    navigation.zoomToFitImage()
                }
                if (!trackingView) {
                    trackingView = true
                    navigation.viewChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                        const {width, height} = this.$imageData()
                        const center = navigation.getCenterPosition()
                        this.$viewState.set({width, height, zoom: navigation.getZoomLevel(), center: {x: center.x, y: center.y}})
                    })
                }
                this.loadingComplete.emit()
            })
    }

    ngOnInit() {
        this.hotkeys
            .addShortcut("Space")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const canvasBase = this.$canvasBase()

                const zoomLevel: number = canvasBase.navigation.getPhysicalZoomLevel()
                // This is needed because of the rounding errors, since the zoom level at 100% is often 0.99999999 instead of 1.
                const epsilon: number = Math.abs(zoomLevel - 1)
                if (epsilon < 0.01) {
                    canvasBase.navigation.zoomToFitImage()
                } else {
                    canvasBase.navigation.physicalZoomTo(1)
                }
            })
    }
}
